import {
  ContentModule,
  useExternalScript,
  useShowInstructions,
} from '@backstage-components/base';
import {useSubscription} from 'observable-hooks';
import {FC, useEffect, useMemo, useState} from 'react';
import {ComponentDefinition, SchemaType} from './IntercomDefinition';

export type IntercomComponentDefinition = ContentModule<'Intercom', SchemaType>;

export const IntercomComponent: FC<IntercomComponentDefinition> = (
  definition
) => {
  const {props} = definition;
  const {appId} = props;
  const [isBooted, setIsBooted] = useState(false);
  const intercomSettings: IntercomSettings = useMemo(() => {
    return {
      app_id: props.appId,
      action_color: props.actionColor,
      alignment: props.alignment,
      background_color: props.backgroundColor,
    };
  }, [props]);
  // Add the Intercom script to the document
  useExternalScript({
    id: 'intercom-component-external',
    src: `https://widget.intercom.io/widget/${appId}`,
    onload: () => {
      if (
        typeof window === 'undefined' ||
        typeof window.Intercom === 'undefined'
      ) {
        return;
      }
      window.Intercom('boot', intercomSettings);
      setIsBooted(true);
    },
    onunload: () => {
      if (
        typeof window === 'undefined' ||
        typeof window.Intercom === 'undefined'
      ) {
        return;
      }
      window.Intercom('shutdown');
      setIsBooted(false);
    },
  });
  // Set the appId in settings
  useEffect(() => {
    // If this is not a window bail out
    if (typeof window === 'undefined') {
      return;
    } else if (typeof window.intercomSettings === 'undefined') {
      window.intercomSettings = {app_id: appId};
    } else {
      window.intercomSettings.app_id = appId;
    }
  }, [appId]);
  // Make calls to `Intercom` when instructions are received
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  useSubscription(observable, (instruction) => {
    // If the Intercom function isn't available yet we can't do much; bail out
    if (
      typeof window === 'undefined' ||
      typeof window.Intercom === 'undefined'
    ) {
      return;
    }
    if (instruction.type === 'Intercom:boot') {
      if (isBooted) {
        window.Intercom('boot', intercomSettings);
      } else {
        window.Intercom('update', intercomSettings);
      }
      setIsBooted(true);
    } else if (instruction.type === 'Intercom:hide') {
      window.Intercom('hide');
    } else if (instruction.type === 'Intercom:show') {
      window.Intercom('show');
    } else if (instruction.type === 'Intercom:showMessages') {
      window.Intercom('showMessages');
    } else if (instruction.type === 'Intercom:trackEvent') {
      window.Intercom('trackEvent', instruction.meta.name);
    } else if (instruction.type === 'Intercom:update') {
      window.Intercom('update');
    } else if (instruction.type === 'Intercom:shutdown') {
      window.Intercom('shutdown');
    }
  });
  // Set up listeners for Intercom to broadcast into local broker
  useEffect(() => {
    // If the Intercom function isn't available yet we can't do much; bail out
    if (
      typeof window === 'undefined' ||
      typeof window.Intercom === 'undefined'
    ) {
      return;
    }
    const handleOnHide = (): void =>
      broadcast({type: 'Intercom:on-hide', meta: {}});
    const handleOnShow = (): void =>
      broadcast({type: 'Intercom:on-show', meta: {}});
    const handleOnUnreadCountChange = (count: number): void =>
      broadcast({type: 'Intercom:on-unread-count-change', meta: {count}});
    if (isBooted) {
      window.Intercom('onHide', handleOnHide);
      window.Intercom('onShow', handleOnShow);
      window.Intercom('onUnreadCountChange', handleOnUnreadCountChange);
    }
  }, [broadcast, isBooted]);
  return null;
};
